import React, { useContext, useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router';
import { Link } from 'react-router';
import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Autocomplete,
} from '@mui/material';
import { Clear, TuneRounded } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import DocumentAddNoteModal from './DocumentAddNoteModal';
import DocumentTable from './DocumentTable';
import DocumentViewDocumentModal from './DocumentViewDocumentModal';
import TopBar from '../common/TopBar';
import usePaginatedDocumentsManifest from '../hooks/usePaginatedDocumentsManifest';
import { DocumentStateContext, DocumentDispatchContext } from '../../context/DocumentContext';
import { useSetTitle } from '../hooks/useSetTitle';
import DocumentTableCreateEB from './DocumentTableCreateEB';
import DocumentTableCreateTrainingSet from './DocumentTableCreateTrainingSet';
import DocumentTableAddToTrainingSet from './DocumentTableAddToTrainingSet';
import DocumentTableBulkStatusUpdate from './DocumentTableBulkStatusUpdate';
import DocumentTableCreateDraftPackage from './DocumentTableCreateDraftPackage';
import DocumentFiltersPopover, {
  DocFilterValues,
  DOC_FILTER_KEYS,
  urlToDocDraft,
  docDraftToUrlParams,
} from './DocumentFiltersPopover';
import useSearchParams from '../hooks/useSearchParams';
import { collapseString } from '../../utils/collapseString';
import { IN_LINEAGE_WITH, IN_RELATIONSHIP_SET_WITH } from './documentUtils';
import { useDebouncedSearchParam } from '../hooks/useDebouncedSearchParam';
import { useDataExportCreate } from '../hooks/useDataExportCreate';
import { addDocumentQueryParams, syncSelectedDocumentParams } from './documentUtils';
import errorResponses from '../../errorResponses';
import { AppContext } from '../../App';
import { featureFlags } from '../../utils/featureFlags';
import { splitByDelimiters } from '../../utils/stringUtils';
import { filterInputSx, dropdownChipSx, activeFilterPillSx, btnSubtlePrimarySx, chipAutocompleteInputSx } from '../common/filterStyles';

const FILTER_LABELS: Record<string, string> = {
  has_been_sent: 'Sent',
  is_supported: 'Supported',
  created_after: 'After',
  created_before: 'Before',
  format_id: 'Format',
  id: 'ID Include',
  exclude_id: 'ID Exclude',
  name: 'Name Contains',
  exclude_name: 'Name Excludes',
  language: 'Language',
  exclude_language: 'Lang Exclude',
  duplicate_key: 'Dup Key',
  exclude_duplicate_key: 'Dup Key Excl',
  document_data_status: 'Data Status',
};

const MULTI_VALUE_DOC_FILTER_KEYS = new Set([
  'id',
  'exclude_id',
  'name',
  'exclude_name',
  'language',
  'exclude_language',
  'duplicate_key',
  'exclude_duplicate_key',
]);

const DocumentTableWrapper = () => {
  const { dpid } = useParams<{ dpid: string }>();

  if (!dpid) {
    throw new Error('dpid is not defined');
  }

  const { setToast, dataPoolId } = useContext(AppContext);
  const dispatch = useContext<React.Dispatch<any>>(DocumentDispatchContext);
  const {
    openAddNoteModal,
    openViewDocumentModal,
    createTrainingSetModal,
    createExtractionBatchModal,
  } = useContext(DocumentStateContext);

  const { search } = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(search);
  const { setSearchParam, removeSearchParamSingle, removeSearchParamMultiple } = useSearchParams();

  const perPage = Number(searchParams.get('perPage')) || 50;
  const page = Number(searchParams.get('page')) || 0;
  const labelContains = searchParams.get('label__contains') || '';

  const selectedIds = searchParams.get('selectedIds')?.split(',') || null;
  const formatId = searchParams.get('format_id') || '';
  const isSupported = searchParams.get('is_supported') || '';
  const includeName = searchParams.get('name')?.split(',') || null;
  const excludeName = searchParams.get('exclude_name')?.split(',') || null;
  const includeId = searchParams.get('id')?.split(',') || null;
  const excludeId = searchParams.get('exclude_id')?.split(',') || null;
  const includeLanguage = searchParams.get('language')?.split(',') || null;
  const excludeLanguage = searchParams.get('exclude_language')?.split(',') || null;
  const includeDuplicateKey = searchParams.get('duplicate_key')?.split(',') || null;
  const excludeDuplicateKey = searchParams.get('exclude_duplicate_key')?.split(',') || null;
  const hasBeenSent = searchParams.get('has_been_sent') || '';
  const createdAfter = searchParams.get('created_after') || '';
  const createdBefore = searchParams.get('created_before') || '';
  const includeDocumentDataStatus = searchParams.get('document_data_status') || '';
  const inRelationshipSetWith = searchParams.get('in_relationship_set_with') || undefined;
  const rootDocumentData = searchParams.get(IN_LINEAGE_WITH) || undefined;

  useSetTitle(`Document Manifest`);

  const [tsMenuAnchor, setTsMenuAnchor] = useState<HTMLElement | null>(null);
  const [addToTsOpen, setAddToTsOpen] = useState(false);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);

  const {
    data: documents,
    isLoading,
    isFetching,
    previousPage: previousPageDoc,
    nextPage: nextPageDoc,
    setPage,
  } = usePaginatedDocumentsManifest({
    limit: perPage,
    dpId: dpid,
    defaultPage: page,
    labelContains,
    hasBeenSent,
    isSupported,
    formatId,
    createdAfter,
    createdBefore,
    includeName: includeName && includeName.length > 0 ? includeName.join(',') : undefined,
    excludeName: excludeName && excludeName.length > 0 ? excludeName.join(',') : undefined,
    includeId: includeId && includeId.length > 0 ? includeId.join(',') : undefined,
    excludeId: excludeId && excludeId.length > 0 ? excludeId.join(',') : undefined,
    includeLanguage: includeLanguage && includeLanguage.length > 0 ? includeLanguage.join(',') : undefined,
    excludeLanguage: excludeLanguage && excludeLanguage.length > 0 ? excludeLanguage.join(',') : undefined,
    includeDuplicateKey:
      includeDuplicateKey && includeDuplicateKey.length > 0 ? includeDuplicateKey.join(',') : undefined,
    excludeDuplicateKey:
      excludeDuplicateKey && excludeDuplicateKey.length > 0 ? excludeDuplicateKey.join(',') : undefined,
    includeDocumentDataStatus: includeDocumentDataStatus || undefined,
    inRelationshipSetWith,
    rootDocumentData,
  });

  const totalDocuments = documents?.count || 0;

  useEffect(() => {
    setPage(page);
  }, [page, setPage]);

  /* ── Debounced label input ── */
  const { localValue: localLabel, setLocalValue: setLocalLabel, debouncedSet: debouncedSetLabel, clear: clearLabel } = useDebouncedSearchParam('label__contains', labelContains);

  /* ── Filters popover ── */
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(null);

  const currentFilterValues = urlToDocDraft(searchParams);

  const handleApplyFilters = (values: DocFilterValues) => {
    const params = new URLSearchParams(search);
    params.set('page', '0');
    DOC_FILTER_KEYS.forEach((key) => params.delete(key));
    const urlParams = docDraftToUrlParams(values);
    Object.entries(urlParams).forEach(([key, val]) => params.set(key, val));
    navigate({ search: params.toString() });
    setPopoverAnchorEl(null);
  };

  const handleDeleteSelectedPill = () => {
    const params = new URLSearchParams(search);

    params.set('page', '0');
    syncSelectedDocumentParams(params, []);

    navigate({ search: params.toString() });
  };

  const popoverFiltersCount = DOC_FILTER_KEYS.filter((k) => searchParams.get(k)).length;
  const hasSelectedDocs = selectedIds && selectedIds.length > 0;
  const hasDocIdFilter = includeId && includeId.length > 0;
  const totalActiveFilters = popoverFiltersCount + (labelContains ? 1 : 0) + (hasSelectedDocs ? 1 : 0) + (hasDocIdFilter ? 1 : 0) + (inRelationshipSetWith ? 1 : 0) + (rootDocumentData ? 1 : 0);

  /* ── Export ── */
  const createDataExportMutation = useDataExportCreate({
    onSuccess: () => {
      setToast({
        variant: 'success',
        open: true,
        message: 'Export in progress. Click `View Exports` to view/download your export.',
      });
    },
    onError: () => {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToExportDocumentManifest,
      });
    },
  });

  const handleExport = () => {
    setExportConfirmOpen(true);
  };

  const handleExportConfirm = () => {
    setExportConfirmOpen(false);
    createDataExportMutation.mutate({
        report_type: 'document_manifest',
        filters: {
          data_pool: dpid,
          ...(inRelationshipSetWith && { in_relationship_set_with: inRelationshipSetWith }),
          ...(rootDocumentData && { in_lineage_with: rootDocumentData }),
          ...addDocumentQueryParams(
            labelContains,
            hasBeenSent,
            isSupported,
            formatId,
            createdAfter,
            createdBefore,
            includeName && includeName.length > 0 ? includeName.join(',') : undefined,
            excludeName && excludeName.length > 0 ? excludeName.join(',') : undefined,
            includeId && includeId.length > 0 ? includeId.join(',') : undefined,
            excludeId && excludeId.length > 0 ? excludeId.join(',') : undefined,
            includeLanguage && includeLanguage.length > 0 ? includeLanguage.join(',') : undefined,
            excludeLanguage && excludeLanguage.length > 0 ? excludeLanguage.join(',') : undefined,
            includeDuplicateKey && includeDuplicateKey.length > 0 ? includeDuplicateKey.join(',') : undefined,
            excludeDuplicateKey && excludeDuplicateKey.length > 0 ? excludeDuplicateKey.join(',') : undefined,
            includeDocumentDataStatus || undefined,
            undefined
          ),
        },
      });
  };

  return (
    <div>
      <TopBar />

      <Box sx={{ backgroundColor: '#F5F6FA', padding: '20px 40px' }}>
        {/* ── Row 1: Title bar + action buttons ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Document Manifest
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#65BBA9', fontWeight: 600 }}
            >
              {isLoading || isFetching ? '...' : (
                <>(<span data-cy="span-document-manifest-count">{totalDocuments.toLocaleString()}</span>)</>
              )}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Training Set dropdown */}
            <Button
              data-cy="btn-manifest-training-set-menu"
              disabled={!hasSelectedDocs}
              startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
              endIcon={<ArrowDropDownIcon sx={{ fontSize: '16px !important', ml: -0.5 }} />}
              onClick={(e) => setTsMenuAnchor(e.currentTarget)}
              sx={btnSubtlePrimarySx}
            >
              Training Set
            </Button>
            <Menu
              anchorEl={tsMenuAnchor}
              open={Boolean(tsMenuAnchor)}
              onClose={() => setTsMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{ paper: { sx: { borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', mt: '4px' } } }}
            >
              <MenuItem
                data-cy="btn-manifest-add-to-training-set"
                onClick={() => { setTsMenuAnchor(null); setAddToTsOpen(true); }}
                sx={{ fontSize: '13px', py: 0.75 }}
              >
                <ListItemIcon><PlaylistAddIcon sx={{ fontSize: '18px', color: '#1D9AAA' }} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '13px' }}>Add to Existing</ListItemText>
              </MenuItem>
              <MenuItem
                data-cy="btn-manifest-create-training-set"
                onClick={() => { setTsMenuAnchor(null); dispatch({ type: 'TOGGLE_CREATE_TRAINING_SET_MODAL' }); }}
                sx={{ fontSize: '13px', py: 0.75 }}
              >
                <ListItemIcon><AddIcon sx={{ fontSize: '18px', color: '#1D9AAA' }} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '13px' }}>Create New</ListItemText>
              </MenuItem>
            </Menu>
            <DocumentTableAddToTrainingSet open={addToTsOpen} onClose={() => setAddToTsOpen(false)} />

            {/* Create Extraction Batch */}
            <Button
              data-cy="btn-manifest-create-extraction-batch"
              disabled={!hasSelectedDocs}
              startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
              onClick={() => dispatch({ type: 'TOGGLE_CREATE_EXTRACTION_BATCH_MODAL' })}
              sx={btnSubtlePrimarySx}
            >
              Create EB
            </Button>

            <DocumentTableBulkStatusUpdate documents={documents?.results || []} />

            <DocumentTableCreateDraftPackage />

            {featureFlags.documentManfiestExportLinks && (
              <>
                <Button
                  data-cy="btn-manifest-export"
                  disabled={isLoading || isFetching}
                  startIcon={<FileDownloadIcon sx={{ fontSize: '16px !important' }} />}
                  onClick={handleExport}
                  sx={btnSubtlePrimarySx}
                >
                  Export
                </Button>
                <Button
                  component={Link}
                  to={`/data-pools/${dataPoolId}/data-exports`}
                  data-cy="btn-manifest-export-view"
                  startIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                  sx={btnSubtlePrimarySx}
                >
                  View Exports
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* ── Row 2: Filter controls — right-aligned ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: totalActiveFilters > 0 ? 1 : 1.5,
            justifyContent: 'flex-end',
          }}
        >
          <Autocomplete
            multiple
            freeSolo
            limitTags={2}
            options={[]}
            size="small"
            value={includeId || []}
            onChange={(_e, newVal, reason) => {
              // Handle chip deletion or Enter key adding a single value
              if (reason === 'removeOption' || reason === 'clear') {
                const params = new URLSearchParams(search);
                params.set('page', '0');
                if (newVal.length > 0) {
                  params.set('id', (newVal as string[]).join(','));
                } else {
                  params.delete('id');
                }
                navigate({ search: params.toString() });
                return;
              }
              // For createOption (Enter), split the new value by comma/space
              if (reason === 'createOption' && newVal.length > 0) {
                const lastVal = newVal[newVal.length - 1] as string;
                const existingVals = (newVal.slice(0, -1) as string[]);
                const parsedVals = splitByDelimiters(lastVal);
                const allVals = [...new Set([...existingVals, ...parsedVals])];
                const params = new URLSearchParams(search);
                params.set('page', '0');
                if (allVals.length > 0) {
                  params.set('id', allVals.join(','));
                } else {
                  params.delete('id');
                }
                navigate({ search: params.toString() });
              }
            }}
            onBlur={(e) => {
              // Auto-add any text in the input on blur
              const inputVal = (e.target as HTMLInputElement).value?.trim();
              if (inputVal) {
                const parsedVals = splitByDelimiters(inputVal);
                if (parsedVals.length > 0) {
                  const existingVals = includeId || [];
                  const allVals = [...new Set([...existingVals, ...parsedVals])];
                  const params = new URLSearchParams(search);
                  params.set('page', '0');
                  params.set('id', allVals.join(','));
                  navigate({ search: params.toString() });
                }
              }
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option}
                  label={option}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '11px',
                    bgcolor: 'rgba(29,154,170,0.08)',
                    color: '#1a2332',
                    '& .MuiChip-deleteIcon': { fontSize: 14, color: '#5f6d7e' },
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={!includeId || includeId.length === 0 ? 'Doc IDs...' : ''}
                variant="outlined"
                data-cy="filter-doc-id-includes"
                onPaste={(e) => {
                  const pastedText = e.clipboardData.getData('text');
                  if (pastedText.includes(',') || pastedText.includes(' ')) {
                    e.preventDefault();
                    const parsedVals = splitByDelimiters(pastedText);
                    if (parsedVals.length > 0) {
                      const existingVals = includeId || [];
                      const allVals = [...new Set([...existingVals, ...parsedVals])];
                      const searchParams = new URLSearchParams(search);
                      searchParams.set('page', '0');
                      searchParams.set('id', allVals.join(','));
                      navigate({ search: searchParams.toString() });
                    }
                  }
                }}
              />
            )}
            sx={chipAutocompleteInputSx}
          />

          <TextField
            size="small"
            placeholder="Search by label..."
            variant="outlined"
            autoComplete="off"
            value={localLabel}
            onChange={(e) => {
              setLocalLabel(e.target.value);
              debouncedSetLabel(e.target.value);
            }}
            data-cy="text-manifest-search-input"
            sx={{ width: 180, ...filterInputSx }}
            InputProps={{
              endAdornment: localLabel && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => clearLabel()}
                    edge="end"
                    sx={{ mr: -1, p: 0.25 }}
                  >
                    <Clear sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Chip
            icon={<TuneRounded sx={{ fontSize: '16px !important' }} />}
            label={popoverFiltersCount > 0 ? `Filters (${popoverFiltersCount})` : 'Filters'}
            onClick={(e) => setPopoverAnchorEl(e.currentTarget)}
            data-cy="btn-more-filters"
            sx={{
              ...dropdownChipSx,
              ...(popoverFiltersCount > 0 && {
                borderColor: '#1D9AAA',
                backgroundColor: 'rgba(29,154,170,0.06)',
              }),
            }}
          />

          <DocumentFiltersPopover
            anchorEl={popoverAnchorEl}
            currentValues={currentFilterValues}
            onClose={() => setPopoverAnchorEl(null)}
            onApply={handleApplyFilters}
          />
        </Box>

        {/* ── Row 3: Active filter pills — right-aligned ── */}
        {totalActiveFilters > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Doc ID pills (managed in main filter bar, not popover) */}
            {includeId && includeId.map((idVal) => (
              <Chip
                key={`id-${idVal}`}
                label={`ID: ${idVal}`}
                onDelete={() => removeSearchParamMultiple(idVal, 'id')}
                data-cy="pill-id"
                data-pill-value={idVal}
                sx={activeFilterPillSx}
              />
            ))}

            {labelContains && (
              <Chip
                label={`Label: ${labelContains}`}
                onDelete={() => clearLabel()}
                data-cy="pill-label"
                sx={activeFilterPillSx}
              />
            )}

            {DOC_FILTER_KEYS.map((key) => {
              const value = searchParams.get(key);
              if (!value) return null;

              if (MULTI_VALUE_DOC_FILTER_KEYS.has(key)) {
                return value
                  .split(',')
                  .map((rawValue) => rawValue.trim())
                  .filter(Boolean)
                  .map((chipValue) => (
                    <Chip
                      key={`${key}-${chipValue}`}
                      label={`${FILTER_LABELS[key]}: ${chipValue}`}
                      onDelete={() => removeSearchParamMultiple(chipValue, key)}
                      data-cy={`pill-${key}`}
                      data-pill-value={chipValue}
                      sx={activeFilterPillSx}
                    />
                  ));
              }

              // For date filters, show a friendlier display
              let displayValue = value;
              if (key === 'created_after' || key === 'created_before') {
                try {
                  displayValue = new Date(value).toLocaleDateString();
                } catch {
                  // keep raw value
                }
              }
              if (key === 'document_data_status') {
                displayValue = value.replace(/_/g, ' ');
              }

              return (
                <Chip
                  key={key}
                  label={`${FILTER_LABELS[key]}: ${displayValue}`}
                  onDelete={() => removeSearchParamSingle(key)}
                  data-cy={`pill-${key}`}
                  sx={activeFilterPillSx}
                />
              );
            })}

            {inRelationshipSetWith && (
              <Chip
                label={`Duplicate set: ${collapseString(inRelationshipSetWith, 4, 4)}`}
                onDelete={() => removeSearchParamSingle(IN_RELATIONSHIP_SET_WITH)}
                data-cy="pill-in-relationship-set-with"
                sx={activeFilterPillSx}
              />
            )}

            {rootDocumentData && (
              <Chip
                label={`Lineage of: ${collapseString(rootDocumentData, 4, 4)}`}
                onDelete={() => removeSearchParamSingle(IN_LINEAGE_WITH)}
                data-cy="pill-root-document-data"
                sx={activeFilterPillSx}
              />
            )}

            {hasSelectedDocs && (
              <Chip
                label={`Selected: ${selectedIds!.length} ${selectedIds!.length > 1 ? 'documents' : 'document'}`}
                onDelete={handleDeleteSelectedPill}
                onClick={() => {
                  const params = new URLSearchParams(search);
                  const selectedIdCsv = selectedIds!.join(',');
                  params.set('page', '0');
                  params.set('selectedOnly', 'true');
                  params.set('id', selectedIdCsv);
                  params.set('selectedIds', selectedIdCsv);
                  navigate({
                    search: params.toString(),
                  });
                }}
                data-cy="pill-selected"
                sx={{ ...activeFilterPillSx, cursor: 'pointer' }}
              />
            )}
          </Box>
        )}

        {/* ── Table ── */}
        <DocumentTable
          totalDocuments={totalDocuments}
          documents={documents?.results || []}
          loading={isLoading || isFetching}
          previousPageDoc={previousPageDoc}
          nextPageDoc={nextPageDoc}
          page={page}
          perPage={perPage}
        />

        {openAddNoteModal ? <DocumentAddNoteModal /> : null}
        {openViewDocumentModal ? <DocumentViewDocumentModal /> : null}
        {createTrainingSetModal ? <DocumentTableCreateTrainingSet /> : null}
        {createExtractionBatchModal ? <DocumentTableCreateEB /> : null}

        <DialogGlynt open={exportConfirmOpen} closeModal={() => setExportConfirmOpen(false)} cypressId="dialog-export-confirm">
          <DialogTitleGlynt title="Generate Export" closeModal={() => setExportConfirmOpen(false)} />
          <DialogContentGlynt>
            <Typography variant="body2" color="textSecondary">
              Are you sure you want to generate an export?
            </Typography>
          </DialogContentGlynt>
          <DialogActionsGlynt
            handleSubmit={handleExportConfirm}
            closeModal={() => setExportConfirmOpen(false)}
            cancelCypressId="btn-export-cancel"
            submitCypressId="btn-export-confirm"
            clearCypressId=""
            submitButtonText="Export"
            submitLoading={createDataExportMutation.isPending}
          />
        </DialogGlynt>
      </Box>
    </div>
  );
};

export default DocumentTableWrapper;
